import '.fa{flex-grow:0;}.ssr-plugin'
import { useTranslations } from '@i18n/useLanguage'
import { ReactNode, useMemo } from 'react'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useAuthState } from '@based/sdk/react'
import { Text } from '@app/components/core/typography/Text'
import { ProfileEdit } from 'functions/app/dashboard/profile/edit'
import { Form } from '@app/components/core/form/Form'
import { BasedAutoSave } from '@app/components/core/form'
import { SelectField } from '@app/components/core/form/fields/SelectField'
import { styled } from 'inlines'
import { PersonalProfileModel } from 'functions/app/dashboard/profile/view'
import { z } from 'zod'

type NotificationsFormValues = {
  updateEmailsEnabled: 'true' | 'false'
  importantNotificationsEmailsEnabled: 'true' | 'false'
  monthlyNewsletterEmailsEnabled: 'true' | 'false'
  crowdBuildingIntroductionEmailsEnabled: 'true' | 'false'
}

const notificationsSchema = z.object({
  updateEmailsEnabled: z.enum(['true', 'false']),
  importantNotificationsEmailsEnabled: z.enum(['true', 'false']),
  monthlyNewsletterEmailsEnabled: z.enum(['true', 'false']),
  crowdBuildingIntroductionEmailsEnabled: z.enum(['true', 'false']),
})

export const NotificationsEditPanel = ({
  profile,
}: {
  profile: PersonalProfileModel
}) => {
  const t = useTranslations().app
  const authState = useAuthState()
  const translations = useTranslations().app.dashboard.preferences

  const selectOptions = useMemo(
    () => [
      {
        value: 'true',
        label: t.common.yes,
      },
      {
        value: 'false',
        label: t.common.no,
      },
    ],
    [t],
  )
  const defaultValues = useMemo<NotificationsFormValues>(
    () => ({
      updateEmailsEnabled: String(profile.updateEmailsEnabled ?? false) as
        | 'true'
        | 'false',
      importantNotificationsEmailsEnabled: String(
        profile.importantNotificationsEmailsEnabled ?? false,
      ) as 'true' | 'false',
      monthlyNewsletterEmailsEnabled: String(
        profile.monthlyNewsletterEmailsEnabled ?? false,
      ) as 'true' | 'false',
      crowdBuildingIntroductionEmailsEnabled: String(
        profile.crowdBuildingIntroductionEmailsEnabled ?? false,
      ) as 'true' | 'false',
    }),
    [
      profile.crowdBuildingIntroductionEmailsEnabled,
      profile.importantNotificationsEmailsEnabled,
      profile.monthlyNewsletterEmailsEnabled,
      profile.updateEmailsEnabled,
    ],
  )

  return (
    <Form<NotificationsFormValues>
      schema={notificationsSchema}
      defaultValues={defaultValues}
      mode="onChange"
      onSubmit={() => {}}
    >
      <BasedAutoSave<NotificationsFormValues>
        functionName="edit-profile"
        debounceMs={300}
        getPayload={(values) => ({
          id: Number(authState.userId),
          value: mapNotificationsFormValues(values),
        })}
      />
      <Column
        alignItems="stretch"
        gap={'1.5rem'}
        style={{
          backgroundColor: 'var(--palette-white)',
          padding: '1.5rem',
          borderRadius: '1.25rem',
          flex: '1 1 30rem',
        }}
      >
        <Title.H4 style={{ marginBottom: 0 }}>
          {translations.notificationSettings}
        </Title.H4>
        <NotificationEdit
          title={translations.updateEmailsTitle}
          subTitle={translations.updateEmailsSubtitle}
        >
          <SelectField<NotificationsFormValues>
            name="updateEmailsEnabled"
            variant="muted-blue"
            options={selectOptions}
          />
        </NotificationEdit>
        <NotificationEdit
          title={translations.importantNotificationsTitle}
          subTitle={translations.importantNotificationsSubtitle}
        >
          <SelectField<NotificationsFormValues>
            name="importantNotificationsEmailsEnabled"
            variant="muted-blue"
            options={selectOptions}
          />
        </NotificationEdit>
        <NotificationEdit
          title={translations.monthlyNewsletterTitle}
          subTitle={translations.monthlyNewsletterSubtitle}
        >
          <SelectField<NotificationsFormValues>
            name="monthlyNewsletterEmailsEnabled"
            variant="muted-blue"
            options={selectOptions}
          />
        </NotificationEdit>
        <NotificationEdit
          title={translations.crowdBuildingIntroductionTitle}
          subTitle={translations.crowdBuildingIntroductionSubtitle}
        >
          <SelectField<NotificationsFormValues>
            name="crowdBuildingIntroductionEmailsEnabled"
            variant="muted-blue"
            options={selectOptions}
          />
        </NotificationEdit>
      </Column>
    </Form>
  )
}

const mapNotificationsFormValues = (
  values: NotificationsFormValues,
): ProfileEdit => ({
  updateEmailsEnabled: values.updateEmailsEnabled === 'true',
  importantNotificationsEmailsEnabled:
    values.importantNotificationsEmailsEnabled === 'true',
  monthlyNewsletterEmailsEnabled:
    values.monthlyNewsletterEmailsEnabled === 'true',
  crowdBuildingIntroductionEmailsEnabled:
    values.crowdBuildingIntroductionEmailsEnabled === 'true',
})

type NotificationEditProps = {
  title: string
  subTitle: string
  children: ReactNode
}

const NotificationEdit = ({
  title,
  subTitle,
  children,
}: NotificationEditProps) => {
  return (
    <Row style={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Column alignItems="stretch" gap={0}>
        <Text.Medium isBold={true}>{title}</Text.Medium>
        <Text.Small>{subTitle}</Text.Small>
      </Column>
      <div className="fa">{children}</div>
    </Row>
  )
}
